import Decimal from "decimal.js";

import type { JournalEntry, JournalLine } from "@/apis/gql/types";
import { TransactionSide } from "@/apis/gql/types";
import { InvoiceType } from "@/lib/constants/invoice-type";

/**
 * Sum debit and credit amounts across journal entries
 */
export function sumJournalEntries(entries: JournalEntry[]) {
  let totalDebit = new Decimal(0);
  let totalCredit = new Decimal(0);
  let originalAmount = new Decimal(0);

  for (const entry of entries) {
    for (const line of entry.journalLines) {
      totalDebit = totalDebit.plus(line.debit ?? 0);
      totalCredit = totalCredit.plus(line.credit ?? 0);
      originalAmount = originalAmount.plus(line.originalAmount ?? 0);
    }
  }

  const balance = totalDebit.minus(totalCredit);

  return {
    debit: totalDebit,
    credit: totalCredit,
    balance,
    originalAmount,
  };
}

/**
 * Sum debit and credit amounts from journal lines directly
 */
export function sumJournalLines(lines: JournalLine[]) {
  let totalDebit = new Decimal(0);
  let totalCredit = new Decimal(0);

  for (const line of lines) {
    totalDebit = totalDebit.plus(line.debit ?? 0);
    totalCredit = totalCredit.plus(line.credit ?? 0);
  }

  const balance = totalDebit.minus(totalCredit);

  return {
    totalDebit,
    totalCredit,
    balance,
  };
}

/**
 * Helper to safely convert nullable numbers to Decimal
 */
export function safeDecimal(value: number | null | undefined): Decimal {
  return new Decimal(value ?? 0);
}

/**
 * Calculate the signed amount for an invoice based on transaction side
 * This handles the complex logic of receivables, payables, and credit notes
 */
function getSignedInvoiceAmount(
  invoice: { invoiceType: string; totalAmount: number },
  transactionSide: TransactionSide,
): Decimal {
  const isReceivable = invoice.invoiceType === InvoiceType.RECEIVABLE;
  const isCreditNote = invoice.totalAmount < 0;
  const totalAmount = new Decimal(Math.abs(invoice.totalAmount));

  if (transactionSide === TransactionSide.CREDIT) {
    if (isReceivable && !isCreditNote) return totalAmount;
    if (isReceivable && isCreditNote) return totalAmount.negated();
    if (!isReceivable && isCreditNote) return totalAmount;
    if (!isReceivable && !isCreditNote) return totalAmount.negated();
  } else {
    if (isReceivable && !isCreditNote) return totalAmount.negated();
    if (isReceivable && isCreditNote) return totalAmount;
    if (!isReceivable && isCreditNote) return totalAmount.negated();
    if (!isReceivable && !isCreditNote) return totalAmount;
  }

  return new Decimal(0);
}

/**
 * Calculate total invoice amount from transactions
 */
export function calculateInvoiceAmount(
  transactions: Array<{
    invoices: Array<{ invoiceType: string; totalAmount: number }>;
    side: TransactionSide;
  }>,
): Decimal {
  return transactions.reduce((total, transaction) => {
    const invoiceSum = transaction.invoices.reduce((sum, invoice) => {
      return sum.plus(getSignedInvoiceAmount(invoice, transaction.side));
    }, new Decimal(0));
    return total.plus(invoiceSum);
  }, new Decimal(0));
}

/**
 * Calculate total transaction amount (signed based on transaction side)
 */
export function calculateTransactionAmount(
  transactions: Array<{ amount: number; side: TransactionSide }>,
): Decimal {
  return transactions.reduce((total, transaction) => {
    const amount =
      transaction.side === TransactionSide.CREDIT
        ? new Decimal(transaction.amount)
        : new Decimal(transaction.amount).negated();
    return total.plus(amount);
  }, new Decimal(0));
}

/**
 * Calculate payment discrepancy between transactions and invoices
 * Returns positive for overpayment, negative for underpayment, zero for exact match
 */
export function calculatePaymentDiscrepancy(
  transactions: Array<{
    invoices: Array<{ invoiceType: string; totalAmount: number }>;
    side: TransactionSide;
    amount: number;
  }>,
) {
  const transactionAmount = calculateTransactionAmount(transactions);
  const invoiceAmount = calculateInvoiceAmount(transactions);
  const discrepancy = transactionAmount.abs().minus(invoiceAmount);

  return {
    discrepancy: discrepancy.toNumber(),
    transactionAmount: transactionAmount.toNumber(),
    invoiceAmount: invoiceAmount.toNumber(),
    isOverpayment: discrepancy.greaterThan(0),
    isUnderpayment: discrepancy.lessThan(0),
    isExactMatch: discrepancy.equals(0),
  };
}
